import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BsRocket } from "react-icons/bs";
import { FaBuilding, FaCompass, FaCrown, FaGift, FaIdBadge, FaStar, FaTimes, FaUser } from "react-icons/fa";
import { GiTreeBeehive } from "react-icons/gi";
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Modal,
  OverlayTrigger,
  Placeholder,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import {
  addGroupBillingMembers,
  BillingInvoiceItem,
  BillingSubscriptionsOverviewResponse,
  createGroupCheckoutSession,
  createTeamCheckoutSession,
  getBillingSubscriptionsOverview,
  getGroupBillingInvoices,
  getGroupBillingMembers,
  GroupBillingMembersResponse,
  SubscriptionSummaryItem,
} from "../../api";
import { PRICING_PLANS } from "../../../constants/platformCopy";
import styles from "./SubscriptionsBillingModal.module.css";


const GiftIcon = FaGift as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const StarIcon = FaStar as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const UserIcon = FaUser as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const BuildingIcon = FaBuilding as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const CompassIcon = FaCompass as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const RocketIcon = BsRocket as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const TeamIcon = GiTreeBeehive as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const CrownIcon = FaCrown as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const BadgeIcon = FaIdBadge as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const RemoveIcon = FaTimes as React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface Props {
  show: boolean;
  onHide: () => void;
  initialPurchasePlan?: PurchasePlanCode;
}

type IndividualPaidPlan = "explorer_monthly" | "solo_monthly";
type TeamPaidPlan = "team_monthly" | "team_annual";
type PurchasePlanCode = IndividualPaidPlan | TeamPaidPlan;

interface PurchasePlanMeta {
  planType: string;
  planName: string;
  amount: string;
  additionalSeat?: string;
}

interface MemberInputRow {
  id: string;
  email: string;
  touched: boolean;
}

interface PurchasePlanOption {
  value: PurchasePlanCode;
  planType: string;
  planName: string;
}

type TeamPurchaseMode = "create" | "existing" | null;

const INVOICE_PAGE_SIZE = 1;

const PURCHASE_PLAN_META: Record<PurchasePlanCode, PurchasePlanMeta> = {
  explorer_monthly: { planType: "Individual", planName: "Explorer", amount: PRICING_PLANS.explorer_monthly.amount },
  solo_monthly: { planType: "Individual", planName: "Solo", amount: PRICING_PLANS.solo_monthly.amount },
  team_monthly: {
    planType: "Team",
    planName: "Monthly",
    amount: PRICING_PLANS.team_monthly.amount,
    additionalSeat: PRICING_PLANS.team_monthly.additionalSeat,
  },
  team_annual: {
    planType: "Team",
    planName: "Annual",
    amount: PRICING_PLANS.team_annual.amount,
    additionalSeat: PRICING_PLANS.team_annual.additionalSeat,
  },
};

const INDIVIDUAL_PLAN_LABELS: Record<string, string> = {
  free: "Free",
  explorer_monthly: "Explorer",
  solo_monthly: "Solo",
  special_individual: "Special",
};

const ALL_PLAN_LABELS: Record<string, string> = {
  ...INDIVIDUAL_PLAN_LABELS,
  team_monthly: "Team",
  team_annual: "Team",
  special_team: "Special Team",
};

const ALLOWED_INDIVIDUAL_ACTIVE_PLAN_CODES: ReadonlySet<string> = new Set([
  "free",
  "explorer_monthly",
  "solo_monthly",
  "special_individual",
]);

const ALLOWED_BUSINESS_ACTIVE_PLAN_CODES: ReadonlySet<string> = new Set([
  "team_monthly",
  "team_annual",
  "special_team",
]);

const RENEWAL_ELIGIBLE_PLAN_CODES: ReadonlySet<string> = new Set([
  "explorer_monthly",
  "solo_monthly",
  "team_monthly",
  "team_annual",
]);

const normalizePlanCode = (planCode?: string | null): string => {
  return (planCode || "free").toLowerCase();
};

const getIndividualPlanName = (planCode: string): string => {
  const normalizedCode = normalizePlanCode(planCode);
  if (INDIVIDUAL_PLAN_LABELS[normalizedCode]) return INDIVIDUAL_PLAN_LABELS[normalizedCode];
  return "Custom";
};

const getPlanName = (planCode: string): string => {
  const normalizedCode = normalizePlanCode(planCode);
  if (ALL_PLAN_LABELS[normalizedCode]) return ALL_PLAN_LABELS[normalizedCode];
  return "Custom";
};

const isPaidPlan = (planCode: string): boolean => {
  return normalizePlanCode(planCode) !== "free";
};

const hasActivePaidSubscription = (subscriptionItem: SubscriptionSummaryItem): boolean => {
  const normalizedState = (subscriptionItem.subscription_state || "").trim().toLowerCase();
  return subscriptionItem.is_paid_plan && normalizedState === "active";
};

type PlanTypeIconKey = "individual" | "business";
type PlanIconKey = "free" | "explorer" | "solo" | "team" | "custom";

const getPlanTypeIconKey = (scope: string): PlanTypeIconKey => {
  return scope === "business" ? "business" : "individual";
};

const getPlanIconKey = (planCode: string): PlanIconKey => {
  const normalizedCode = normalizePlanCode(planCode);
  if (normalizedCode === "free") return "free";
  if (normalizedCode === "explorer_monthly") return "explorer";
  if (normalizedCode === "solo_monthly") return "solo";
  if (normalizedCode === "team_monthly" || normalizedCode === "team_annual") {
    return "team";
  }
  return "custom";
};

const renderPlanTypeIcon = (iconKey: PlanTypeIconKey, className: string): React.JSX.Element => {
  if (iconKey === "business") return <BuildingIcon className={className} aria-hidden />;
  return <UserIcon className={className} aria-hidden />;
};

const renderPlanIcon = (iconKey: PlanIconKey, className: string): React.JSX.Element => {
  if (iconKey === "team") return <TeamIcon className={className} aria-hidden />;
  if (iconKey === "free") return <GiftIcon className={className} aria-hidden />;
  if (iconKey === "explorer") return <CompassIcon className={className} aria-hidden />;
  if (iconKey === "solo") return <RocketIcon className={className} aria-hidden />;
  return <StarIcon className={className} aria-hidden />;
};

const getActiveSubscriptionOptionLabel = (option: SubscriptionSummaryItem): string => {
  if (option.scope === "individual") {
    return getIndividualPlanName(option.plan_code);
  }
  return `Team (${option.org_name || "Organization"})`;
};

const renderActiveSubscriptionOptionLabel = (option: SubscriptionSummaryItem): React.JSX.Element => {
  const planTypeIconKey = getPlanTypeIconKey(option.scope);
  return (
    <span className={styles.subscriptionOptionLabel}>
      {renderPlanTypeIcon(planTypeIconKey, styles.planTypeIcon)}
      <span>{getActiveSubscriptionOptionLabel(option)}</span>
    </span>
  );
};

const isAllowedActiveSubscriptionOption = (option: SubscriptionSummaryItem): boolean => {
  const normalizedPlanCode = normalizePlanCode(option.plan_code);
  if (option.scope === "individual") {
    return ALLOWED_INDIVIDUAL_ACTIVE_PLAN_CODES.has(normalizedPlanCode);
  }
  if (option.scope === "business") {
    return ALLOWED_BUSINESS_ACTIVE_PLAN_CODES.has(normalizedPlanCode);
  }
  return false;
};

const buildActiveSubscriptionOptions = (
  overviewPayload: BillingSubscriptionsOverviewResponse,
): SubscriptionSummaryItem[] => {
  return [overviewPayload.individual_subscription, ...overviewPayload.business_subscriptions]
    .filter(isAllowedActiveSubscriptionOption);
};

const getPurchasePlanOptionLabel = (option: PurchasePlanOption): string => {
  if (option.value === "explorer_monthly") return "Explorer";
  if (option.value === "solo_monthly") return "Solo";
  if (option.value === "team_monthly") return "Team (Monthly)";
  if (option.value === "team_annual") return "Team (Annual)";
  return `${option.planName} (${option.planType})`;
};

const getPurchasePlanOptionIconKey = (option: PurchasePlanOption): PlanTypeIconKey => {
  return option.value.startsWith("team_") ? "business" : "individual";
};

const renderPurchasePlanOptionLabel = (option: PurchasePlanOption): React.JSX.Element => {
  return (
    <span className={styles.subscriptionOptionLabel}>
      {renderPlanTypeIcon(getPurchasePlanOptionIconKey(option), styles.planTypeIcon)}
      <span>{getPurchasePlanOptionLabel(option)}</span>
    </span>
  );
};

const formatDate = (isoDate?: string | null): string => {
  if (!isoDate) return "Unavailable";
  const parsedDate = new Date(isoDate);
  if (Number.isNaN(parsedDate.getTime())) return "Unavailable";
  return parsedDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateOrNull = (isoDate?: string | null): string | null => {
  if (!isoDate) return null;
  const parsedDate = new Date(isoDate);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return parsedDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrency = (amountCents: number, currency: string): string => {
  const amount = amountCents / 100;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);
};

const normalizeTeamName = (teamNameValue: string): string => {
  return teamNameValue.trim().toLowerCase();
};

const getCreateTeamNameValidationMessage = (teamNameValue: string): string => {
  if (!teamNameValue) return "";
  if (teamNameValue.startsWith("-")) {
    return "Team name cannot start with a hyphen";
  }
  if (teamNameValue.endsWith("-")) {
    return "Team name cannot end with a hyphen";
  }
  if (teamNameValue.includes("--")) {
    return "Team name cannot contain consecutive hyphens";
  }
  if (teamNameValue.includes("_")) {
    return "Underscores are not allowed. Use letters, numbers, and hyphens only, and do not use consecutive hyphens";
  }
  if (!/^[A-Za-z0-9-]+$/.test(teamNameValue)) {
    const invalidCharacters = Array.from(
      new Set(teamNameValue.replace(/[A-Za-z0-9-]/g, "").split("")),
    ).join(" ");
    if (invalidCharacters) {
      return `Invalid character(s): ${invalidCharacters}\nUse letters, numbers, and hyphens only`;
    }
    return "Team name can only contain letters, numbers, and hyphens, and cannot contain consecutive hyphens";
  }
  return "";
};

const isValidEmailInput = (emailValue: string): boolean => {
  if (!emailValue) return false;
  const normalizedEmail = emailValue.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
};

const getMemberRoleDisplay = (
  role: string,
): { label: string; iconClassName: string; icon: React.JSX.Element } => {
  const normalizedRole = role.trim().toLowerCase();
  if (normalizedRole === "owner" || normalizedRole === "admin") {
    return {
      label: "Admin",
      iconClassName: styles.memberRoleOwner,
      icon: <CrownIcon aria-hidden className={styles.memberRoleGlyph} />,
    };
  }

  return {
    label: "Member",
    iconClassName: styles.memberRoleMember,
    icon: <BadgeIcon aria-hidden className={styles.memberRoleGlyph} />,
  };
};

const MAX_ADDITIONAL_TEAM_MEMBERS = 5;

const createMemberInputRow = (): MemberInputRow => {
  return {
    id: `member-input-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    email: "",
    touched: false,
  };
};

const SubscriptionsBillingModal: React.FC<Props> = ({ show, onHide, initialPurchasePlan }) => {
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");
  const [overviewData, setOverviewData] = useState<BillingSubscriptionsOverviewResponse | null>(null);
  const [selectedKey, setSelectedKey] = useState("individual");

  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");
  const [invoices, setInvoices] = useState<BillingInvoiceItem[]>([]);
  const [showOldInvoices, setShowOldInvoices] = useState(false);

  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const [membersData, setMembersData] = useState<GroupBillingMembersResponse | null>(null);
  const [memberInputs, setMemberInputs] = useState<MemberInputRow[]>([createMemberInputRow()]);
  const [memberValidationAttempted, setMemberValidationAttempted] = useState(false);

  const [busyAction, setBusyAction] = useState<
    "purchase" | "add-members" | null
  >(null);
  const [errorText, setErrorText] = useState("");
  const [successText, setSuccessText] = useState("");

  const [purchasePlan, setPurchasePlan] = useState<PurchasePlanCode>("team_monthly");
  const [teamPurchaseMode, setTeamPurchaseMode] = useState<TeamPurchaseMode>(null);
  const [teamOrgName, setTeamOrgName] = useState("");
  const [selectedExistingTeamKey, setSelectedExistingTeamKey] = useState("");
  const teamSeatCount = 6;
  const renewalRefreshAttemptsRef = useRef<Set<string>>(new Set());

  const options = useMemo<SubscriptionSummaryItem[]>(() => {
    if (!overviewData) return [];
    return buildActiveSubscriptionOptions(overviewData);
  }, [overviewData]);

  const selectedOption = useMemo(() => {
    return options.find((option) => option.key === selectedKey) || options[0] || null;
  }, [options, selectedKey]);
  const selectedPlanIconKey = selectedOption ? getPlanIconKey(selectedOption.plan_code) : null;

  const selectedNormalizedPlan = normalizePlanCode(selectedOption?.plan_code);
  const normalizedIndividualPlan = normalizePlanCode(overviewData?.individual_subscription.plan_code);
  const renewalDateLabel = formatDateOrNull(selectedOption?.renewal_date);
  const shouldShowRenewal = RENEWAL_ELIGIBLE_PLAN_CODES.has(selectedNormalizedPlan) && Boolean(renewalDateLabel);
  const canShowIndividualPurchasePlans = normalizedIndividualPlan === "free";
  const isSelectedPurchasePlanTeam = ["team_monthly", "team_annual"].includes(purchasePlan);
  const individualGroupId = overviewData?.individual_subscription.group_id || "";
  const existingUnpaidTeamOptions = useMemo<SubscriptionSummaryItem[]>(() => {
    if (!overviewData) return [];
    return overviewData.business_subscriptions.filter(
      (businessSubscription) => !hasActivePaidSubscription(businessSubscription),
    );
  }, [overviewData]);
  const sortedExistingUnpaidTeamOptions = useMemo<SubscriptionSummaryItem[]>(() => {
    return [...existingUnpaidTeamOptions].sort((leftOption, rightOption) => {
      const leftOrgName = (leftOption.org_name || "Organization").trim().toLowerCase();
      const rightOrgName = (rightOption.org_name || "Organization").trim().toLowerCase();
      return leftOrgName.localeCompare(rightOrgName);
    });
  }, [existingUnpaidTeamOptions]);
  const hasExistingUnpaidTeams = existingUnpaidTeamOptions.length > 0;
  const selectedExistingTeamOption = useMemo(() => {
    if (!selectedExistingTeamKey) return null;
    return sortedExistingUnpaidTeamOptions.find((existingTeamOption) => existingTeamOption.key === selectedExistingTeamKey) || null;
  }, [selectedExistingTeamKey, sortedExistingUnpaidTeamOptions]);
  const existingBusinessTeamNames = useMemo<Set<string>>(() => {
    const normalizedTeamNames = (overviewData?.business_subscriptions || [])
      .map((businessSubscription) => normalizeTeamName(businessSubscription.org_name || ""))
      .filter((teamName) => teamName.length > 0);
    return new Set(normalizedTeamNames);
  }, [overviewData?.business_subscriptions]);
  const normalizedEnteredTeamName = useMemo(() => {
    return normalizeTeamName(teamOrgName);
  }, [teamOrgName]);
  const createTeamNameValidationMessage = useMemo(() => {
    if (!(isSelectedPurchasePlanTeam && teamPurchaseMode === "create")) {
      return "";
    }
    if (!normalizedEnteredTeamName) {
      return "";
    }
    return getCreateTeamNameValidationMessage(normalizedEnteredTeamName);
  }, [isSelectedPurchasePlanTeam, normalizedEnteredTeamName, teamPurchaseMode]);
  const hasInvalidCreateTeamName = createTeamNameValidationMessage.length > 0;
  const matchedExistingTeamOption = useMemo(() => {
    if (!normalizedEnteredTeamName) {
      return null;
    }
    return sortedExistingUnpaidTeamOptions.find(
      (existingTeamOption) => normalizeTeamName(existingTeamOption.org_name || "") === normalizedEnteredTeamName,
    ) || null;
  }, [normalizedEnteredTeamName, sortedExistingUnpaidTeamOptions]);
  const hasDuplicateCreateTeamName =
    isSelectedPurchasePlanTeam
    && teamPurchaseMode === "create"
    && normalizedEnteredTeamName.length > 0
    && existingBusinessTeamNames.has(normalizedEnteredTeamName);
  const canPurchaseSelectedTeamPlan = useMemo(() => {
    if (!isSelectedPurchasePlanTeam) {
      return true;
    }
    if (teamPurchaseMode === "create") {
      return teamOrgName.trim().length > 0 && !hasDuplicateCreateTeamName && !hasInvalidCreateTeamName;
    }
    if (teamPurchaseMode === "existing") {
      return Boolean(selectedExistingTeamOption?.org_name?.trim()) && Boolean(selectedExistingTeamOption?.is_owner_or_admin);
    }
    return false;
  }, [
    isSelectedPurchasePlanTeam,
    hasDuplicateCreateTeamName,
    hasInvalidCreateTeamName,
    selectedExistingTeamOption?.is_owner_or_admin,
    selectedExistingTeamOption?.org_name,
    teamOrgName,
    teamPurchaseMode,
  ]);

  const purchasePlanOptions = useMemo<Array<PurchasePlanOption>>(() => {
    const optionsList: Array<PurchasePlanOption> = [];
    if (canShowIndividualPurchasePlans) {
      optionsList.push(
        {
          value: "explorer_monthly",
          planType: PURCHASE_PLAN_META.explorer_monthly.planType,
          planName: PURCHASE_PLAN_META.explorer_monthly.planName,
        },
        {
          value: "solo_monthly",
          planType: PURCHASE_PLAN_META.solo_monthly.planType,
          planName: PURCHASE_PLAN_META.solo_monthly.planName,
        },
      );
    }
    optionsList.push(
      {
        value: "team_monthly",
        planType: PURCHASE_PLAN_META.team_monthly.planType,
        planName: PURCHASE_PLAN_META.team_monthly.planName,
      },
      {
        value: "team_annual",
        planType: PURCHASE_PLAN_META.team_annual.planType,
        planName: PURCHASE_PLAN_META.team_annual.planName,
      },
    );
    return optionsList;
  }, [canShowIndividualPurchasePlans]);

  const selectedPurchasePlanMeta = PURCHASE_PLAN_META[purchasePlan];

  const selectedHasPaidPlan = selectedOption ? isPaidPlan(selectedOption.plan_code) : false;
  const hasActivePaidTeamPlan = useMemo((): boolean => {
    if (!overviewData) return false;
    return overviewData.business_subscriptions.some((businessSubscription) =>
      hasActivePaidSubscription(businessSubscription),
    );
  }, [overviewData]);
  const shouldShowFreeIndividualUpsellInfo =
    selectedOption?.scope === "individual"
    && selectedNormalizedPlan === "free"
    && !hasActivePaidTeamPlan;
  const activeMembers = useMemo(() => {
    if (!membersData) return [];
    return membersData.members.filter((member) => member.is_active);
  }, [membersData]);
  const activeNonOwnerMembers = useMemo(() => {
    return activeMembers.filter((member) => member.role.toLowerCase() !== "owner");
  }, [activeMembers]);
  const existingMemberEmailSet = useMemo(() => {
    return new Set(
      activeMembers
        .map((member) => member.email.trim().toLowerCase())
        .filter((memberEmail) => memberEmail.length > 0),
    );
  }, [activeMembers]);
  const normalizedMemberInputs = useMemo(() => {
    return memberInputs.map((memberInputRow) => memberInputRow.email.trim().toLowerCase());
  }, [memberInputs]);
  const typedInputEmailCounts = useMemo(() => {
    const emailCounts = new Map<string, number>();
    normalizedMemberInputs.forEach((normalizedMemberEmail) => {
      if (!normalizedMemberEmail) return;
      emailCounts.set(normalizedMemberEmail, (emailCounts.get(normalizedMemberEmail) || 0) + 1);
    });
    return emailCounts;
  }, [normalizedMemberInputs]);
  const availableMemberSlots = useMemo(() => {
    const remainingCapacity = Math.max(0, MAX_ADDITIONAL_TEAM_MEMBERS - activeNonOwnerMembers.length);
    const maxFromBackend = membersData?.max_additional_members_per_request ?? MAX_ADDITIONAL_TEAM_MEMBERS;
    return Math.max(0, Math.min(remainingCapacity, maxFromBackend));
  }, [activeNonOwnerMembers.length, membersData?.max_additional_members_per_request]);
  const hasInvalidMemberEmailInput = useMemo(() => {
    return memberInputs.some((memberInputRow) => {
      const normalizedMemberEmail = memberInputRow.email.trim();
      if (!normalizedMemberEmail) return false;
      return !isValidEmailInput(normalizedMemberEmail);
    });
  }, [memberInputs]);
  const hasDuplicateMemberEmailInput = useMemo(() => {
    return normalizedMemberInputs.some((normalizedMemberEmail) => {
      if (!normalizedMemberEmail) return false;
      if (existingMemberEmailSet.has(normalizedMemberEmail)) return true;
      return (typedInputEmailCounts.get(normalizedMemberEmail) || 0) > 1;
    });
  }, [existingMemberEmailSet, normalizedMemberInputs, typedInputEmailCounts]);
  const hasExistingTeamMemberEmailInput = useMemo(() => {
    return normalizedMemberInputs.some((normalizedMemberEmail) => {
      if (!normalizedMemberEmail) return false;
      return existingMemberEmailSet.has(normalizedMemberEmail);
    });
  }, [existingMemberEmailSet, normalizedMemberInputs]);
  const hasRepeatedEnteredEmailInput = useMemo(() => {
    return normalizedMemberInputs.some((normalizedMemberEmail) => {
      if (!normalizedMemberEmail) return false;
      return (typedInputEmailCounts.get(normalizedMemberEmail) || 0) > 1;
    });
  }, [normalizedMemberInputs, typedInputEmailCounts]);
  const hasEmptyMemberEmailInput = useMemo(() => {
    return memberInputs.some((memberInputRow) => memberInputRow.email.trim().length === 0);
  }, [memberInputs]);
  const hasTouchedMemberInput = useMemo(() => {
    return memberInputs.some((memberInputRow) => memberInputRow.touched);
  }, [memberInputs]);
  const shouldShowMemberValidation = memberValidationAttempted || hasTouchedMemberInput;
  const memberInputValidationMessage = useMemo(() => {
    if (!shouldShowMemberValidation) {
      return "";
    }
    if (hasInvalidMemberEmailInput) {
      return "Enter a valid email address in each filled field";
    }
    if (hasExistingTeamMemberEmailInput) {
      return "Entered email ID is already part of the team";
    }
    if (hasRepeatedEnteredEmailInput) {
      return "Looks like one of the email IDs is entered more than once";
    }
    return "";
  }, [
    hasExistingTeamMemberEmailInput,
    hasInvalidMemberEmailInput,
    hasRepeatedEnteredEmailInput,
    shouldShowMemberValidation,
  ]);

  const sortedInvoices = useMemo<BillingInvoiceItem[]>(() => {
    const toTimestamp = (dateValue?: string | null): number => {
      if (!dateValue) {
        return 0;
      }
      const parsedTimestamp = new Date(dateValue).getTime();
      return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
    };

    return [...invoices].sort((leftInvoice, rightInvoice) => {
      const leftTimestamp = toTimestamp(leftInvoice.created_at);
      const rightTimestamp = toTimestamp(rightInvoice.created_at);
      return rightTimestamp - leftTimestamp;
    });
  }, [invoices]);
  const hasOlderInvoices = sortedInvoices.length > INVOICE_PAGE_SIZE;
  const oldInvoicesCount = Math.max(0, sortedInvoices.length - INVOICE_PAGE_SIZE);
  const visibleInvoices = useMemo<BillingInvoiceItem[]>(() => {
    if (showOldInvoices) {
      return sortedInvoices;
    }
    return sortedInvoices.slice(0, INVOICE_PAGE_SIZE);
  }, [showOldInvoices, sortedInvoices]);

  const loadOverview = useCallback(async (preferredSelectedKey?: string): Promise<void> => {
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const response = await getBillingSubscriptionsOverview();
      const payload = response.data;
      setOverviewData(payload);
      const activeOptions = buildActiveSubscriptionOptions(payload);
      const fallbackSelectionKey = activeOptions[0]?.key || payload.individual_subscription.key;
      setSelectedKey((currentSelectedKey) => {
        const requestedSelectedKey = preferredSelectedKey || currentSelectedKey;
        const hasRequestedSelection = activeOptions
          .some((option) => option.key === requestedSelectedKey);
        return hasRequestedSelection ? requestedSelectedKey : fallbackSelectionKey;
      });
    } catch (error: any) {
      setOverviewError(error?.response?.data?.detail || "Failed to load subscriptions.");
      setOverviewData(null);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async (groupId: string): Promise<void> => {
    setInvoiceLoading(true);
    setInvoiceError("");
    try {
      const response = await getGroupBillingInvoices(groupId, 10);
      setInvoices(response.data.invoices || []);
    } catch (error: any) {
      setInvoiceError(error?.response?.data?.detail || "Failed to load invoices.");
      setInvoices([]);
    } finally {
      setInvoiceLoading(false);
    }
  }, []);

  const loadMembers = useCallback(async (groupId: string): Promise<void> => {
    setMembersLoading(true);
    setMembersError("");
    try {
      const response = await getGroupBillingMembers(groupId);
      setMembersData(response.data);
    } catch (error: any) {
      setMembersError(error?.response?.data?.detail || "Failed to load members.");
      setMembersData(null);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    setErrorText("");
    setSuccessText("");
    setTeamPurchaseMode(null);
    setSelectedExistingTeamKey("");
    setTeamOrgName("");
    renewalRefreshAttemptsRef.current = new Set();
    void loadOverview();
  }, [loadOverview, show]);

  useEffect(() => {
    if (!show || !selectedOption) {
      return;
    }

    setShowOldInvoices(false);

    if (selectedOption.is_paid_plan) {
      const refreshInvoicesAndRenewalDate = async (): Promise<void> => {
        await loadInvoices(selectedOption.group_id);

        const selectedNormalizedCode = normalizePlanCode(selectedOption.plan_code);
        const needsRenewalRefresh =
          RENEWAL_ELIGIBLE_PLAN_CODES.has(selectedNormalizedCode)
          && !selectedOption.renewal_date
          && !renewalRefreshAttemptsRef.current.has(selectedOption.key);

        if (needsRenewalRefresh) {
          renewalRefreshAttemptsRef.current.add(selectedOption.key);
          await loadOverview(selectedOption.key);
        }
      };

      void refreshInvoicesAndRenewalDate();
    } else {
      setInvoiceLoading(false);
      setInvoiceError("");
      setInvoices([]);
    }

    if (selectedOption.scope === "business" && selectedOption.is_owner_or_admin) {
      void loadMembers(selectedOption.group_id);
    } else {
      setMembersLoading(false);
      setMembersError("");
      setMembersData(null);
    }
  }, [loadInvoices, loadMembers, loadOverview, show, selectedOption]);

  useEffect(() => {
    if (!show) {
      return;
    }

    if (initialPurchasePlan) {
      const canUseInitialPurchasePlan = purchasePlanOptions.some((option) => option.value === initialPurchasePlan);
      if (canUseInitialPurchasePlan) {
        if (purchasePlan !== initialPurchasePlan) {
          setPurchasePlan(initialPurchasePlan);
        }
        return;
      }
    }

    const selectedStillAvailable = purchasePlanOptions.some((option) => option.value === purchasePlan);
    if (!selectedStillAvailable && purchasePlanOptions.length > 0) {
      setPurchasePlan(purchasePlanOptions[0].value);
    }
  }, [initialPurchasePlan, purchasePlan, purchasePlanOptions, show]);

  useEffect(() => {
    if (!isSelectedPurchasePlanTeam) {
      setTeamPurchaseMode(null);
      setSelectedExistingTeamKey("");
      setTeamOrgName("");
      return;
    }

    if (!hasExistingUnpaidTeams && teamPurchaseMode === "existing") {
      setTeamPurchaseMode(null);
      setSelectedExistingTeamKey("");
    }

    const selectedTeamStillExists = existingUnpaidTeamOptions.some(
      (existingTeamOption) => existingTeamOption.key === selectedExistingTeamKey,
    );
    if (selectedExistingTeamKey && !selectedTeamStillExists) {
      setSelectedExistingTeamKey("");
    }
  }, [
    existingUnpaidTeamOptions,
    hasExistingUnpaidTeams,
    isSelectedPurchasePlanTeam,
    selectedExistingTeamKey,
    teamPurchaseMode,
  ]);

  useEffect(() => {
    setMemberInputs([createMemberInputRow()]);
    setMemberValidationAttempted(false);
  }, [selectedOption?.group_id, show]);

  const handlePurchase = async (): Promise<void> => {
    if (!purchasePlan) return;

    const checkoutReturnBaseUrl = `${window.location.origin}/dashboard`;
    const checkoutQueryParams = new URLSearchParams({ billing_checkout: "success" });
    const cancelCheckoutQueryParams = new URLSearchParams({ billing_checkout: "cancel" });
    let successUrl = `${checkoutReturnBaseUrl}?${checkoutQueryParams.toString()}`;
    let cancelUrl = `${checkoutReturnBaseUrl}?${cancelCheckoutQueryParams.toString()}`;

    setBusyAction("purchase");
    setErrorText("");
    setSuccessText("");

    if (purchasePlan === "explorer_monthly" || purchasePlan === "solo_monthly") {
      if (!individualGroupId) {
        setErrorText("Individual billing group is unavailable. Please refresh and try again.");
        setBusyAction(null);
        return;
      }

      try {
        const response = await createGroupCheckoutSession(individualGroupId, {
          plan_code: purchasePlan,
          seat_quantity: 1,
          success_url: successUrl,
          cancel_url: cancelUrl,
        });

        const checkoutUrl = response.data?.checkout_url;
        if (checkoutUrl) {
          window.location.assign(checkoutUrl);
        } else {
          setSuccessText("Checkout session created.");
        }
      } catch (error: any) {
        setErrorText(error?.response?.data?.detail || "Unable to start individual checkout.");
      } finally {
        setBusyAction(null);
      }
      return;
    }

    const normalizedOrgName = normalizeTeamName(teamOrgName);
    const resolvedTeamName = teamPurchaseMode === "create" ? normalizedOrgName : "";

    if (isSelectedPurchasePlanTeam && teamPurchaseMode === "create" && resolvedTeamName) {
      checkoutQueryParams.set("billing_team_mode", "create");
      checkoutQueryParams.set("billing_team_name", resolvedTeamName);
      cancelCheckoutQueryParams.set("billing_team_mode", "create");
      cancelCheckoutQueryParams.set("billing_team_name", resolvedTeamName);
      successUrl = `${checkoutReturnBaseUrl}?${checkoutQueryParams.toString()}`;
      cancelUrl = `${checkoutReturnBaseUrl}?${cancelCheckoutQueryParams.toString()}`;
    }

    if (teamPurchaseMode === "existing") {
      if (!selectedExistingTeamOption) {
        setErrorText("Select an existing team to purchase a team plan.");
        setBusyAction(null);
        return;
      }

      if (!selectedExistingTeamOption.is_owner_or_admin) {
        setErrorText("Only team owners/admins can purchase a team plan.");
        setBusyAction(null);
        return;
      }

      try {
        const response = await createGroupCheckoutSession(selectedExistingTeamOption.group_id, {
          plan_code: purchasePlan as TeamPaidPlan,
          seat_quantity: Math.max(6, teamSeatCount),
          success_url: successUrl,
          cancel_url: cancelUrl,
        });

        const checkoutUrl = response.data?.checkout_url;
        if (checkoutUrl) {
          window.location.assign(checkoutUrl);
        } else {
          setSuccessText("Team checkout session created.");
        }
      } catch (error: any) {
        setErrorText(error?.response?.data?.detail || "Unable to start team checkout.");
      } finally {
        setBusyAction(null);
      }
      return;
    }

    if (!resolvedTeamName) {
      setErrorText(
        teamPurchaseMode === null
          ? "Select how you want to set up your team plan."
          : "Enter a team name to purchase a team plan.",
      );
      setBusyAction(null);
      return;
    }

    if (teamPurchaseMode === "create" && hasInvalidCreateTeamName) {
      setErrorText(createTeamNameValidationMessage || "Enter a valid team name to purchase a team plan.");
      setBusyAction(null);
      return;
    }

    if (teamPurchaseMode === "create" && hasDuplicateCreateTeamName) {
      setErrorText("A team with this name already exists. Select it from Existing team.");
      setBusyAction(null);
      return;
    }

    try {
      const response = await createTeamCheckoutSession({
        org_name: resolvedTeamName,
        plan_code: purchasePlan as TeamPaidPlan,
        seat_quantity: Math.max(6, teamSeatCount),
        success_url: successUrl,
        cancel_url: cancelUrl,
      });

      const checkoutUrl = response.data?.checkout_url;
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
      } else {
        setSuccessText("Team checkout session created.");
      }
    } catch (error: any) {
      setErrorText(error?.response?.data?.detail || "Unable to start team checkout.");
    } finally {
      setBusyAction(null);
    }
  };

  const handleAddMembers = async (): Promise<void> => {
    if (!selectedOption || selectedOption.scope !== "business") return;

    setMemberValidationAttempted(true);

    if (availableMemberSlots <= 0) {
      setErrorText("No member slots are available for this team.");
      return;
    }

    const normalizedInputs = memberInputs
      .map((memberInputRow) => memberInputRow.email.trim().toLowerCase())
      .filter(Boolean);
    const uniqueEmails = Array.from(new Set(normalizedInputs));

    if (normalizedInputs.length === 0) {
      setErrorText("Enter at least one member email.");
      return;
    }

    if (normalizedInputs.some((normalizedMemberEmail) => existingMemberEmailSet.has(normalizedMemberEmail))) {
      setErrorText("Entered email ID is already part of the team.");
      return;
    }

    if (normalizedInputs.length !== uniqueEmails.length) {
      setErrorText("Looks like the same email ID was entered more than once.");
      return;
    }

    if (uniqueEmails.length > availableMemberSlots) {
      setErrorText(`You can add up to ${availableMemberSlots} more member(s).`);
      return;
    }

    setBusyAction("add-members");
    setErrorText("");
    setSuccessText("");

    try {
      const addResponse = await addGroupBillingMembers(selectedOption.group_id, {
        member_emails: uniqueEmails,
      });
      const successfulAdds = addResponse.data.results.filter((item) => item.added).length;
      setSuccessText(`Processed ${uniqueEmails.length} request(s). Added/activated: ${successfulAdds}.`);
      setMemberInputs([createMemberInputRow()]);
      setMemberValidationAttempted(false);
      await loadMembers(selectedOption.group_id);
    } catch (error: any) {
      setErrorText(error?.response?.data?.detail || "Unable to add members.");
    } finally {
      setBusyAction(null);
    }
  };

  const handleMemberInputChange = (rowId: string, value: string): void => {
    setMemberInputs((previousInputs) =>
      previousInputs.map((previousRow) => (previousRow.id === rowId ? { ...previousRow, email: value } : previousRow)),
    );
  };

  const handleMemberInputBlur = (rowId: string): void => {
    setMemberInputs((previousInputs) =>
      previousInputs.map((previousRow) => (previousRow.id === rowId ? { ...previousRow, touched: true } : previousRow)),
    );
  };

  const handleAddMemberInputRow = (): void => {
    setMemberInputs((previousInputs) => {
      if (previousInputs.length >= availableMemberSlots) {
        return previousInputs;
      }
      return [...previousInputs, createMemberInputRow()];
    });
  };

  const handleRemoveMemberInputRow = (rowId: string): void => {
    setMemberInputs((previousInputs) => {
      if (previousInputs.length <= 1) {
        return previousInputs;
      }
      return previousInputs.filter((previousRow) => previousRow.id !== rowId);
    });
  };

  const handleSelectCreateTeamMode = (): void => {
    setTeamPurchaseMode("create");
    setSelectedExistingTeamKey("");
  };

  const handleSelectExistingTeamMode = (): void => {
    setTeamPurchaseMode("existing");
    setTeamOrgName("");
    setSelectedExistingTeamKey("");
  };

  const handleUseExistingMatchedTeam = (): void => {
    if (!matchedExistingTeamOption) {
      return;
    }
    setTeamPurchaseMode("existing");
    setSelectedExistingTeamKey(matchedExistingTeamOption.key);
    setTeamOrgName("");
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName={styles.modalDialog}
      contentClassName={styles.modalContent}
      backdropClassName={styles.modalBackdrop}
    >
      <Modal.Header className={styles.modalHeader} closeButton>
        <Modal.Title className={styles.modalTitle}>Subscriptions and Billing</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.modalScrollArea}>
          {errorText && <Alert className={styles.alertBox} variant="danger">{errorText}</Alert>}
          {successText && <Alert className={styles.alertBox} variant="success">{successText}</Alert>}

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Purchase a new plan</div>

            {overviewLoading ? (
              <Placeholder animation="glow">
                <Placeholder className={styles.skeletonBar} xs={12} />
                <Placeholder className={styles.skeletonBar} xs={10} />
                <Placeholder className={styles.skeletonBar} xs={8} />
              </Placeholder>
            ) : overviewError || !overviewData ? (
              <Alert variant="warning" className={styles.sectionAlert}>
                We could not load your active subscriptions yet. Please retry to continue with purchase.
                <div className={styles.actionsRow}>
                  <Button className={styles.secondaryActionBtn} onClick={() => void loadOverview()}>
                    Retry
                  </Button>
                </div>
              </Alert>
            ) : (
              <>
                <Form.Group className={styles.formGroupCompact}>
                  <div className={styles.purchasePlanRow}>
                    <Dropdown className={`${styles.premiumDropdown} ${styles.purchasePlanSelect}`}>
                      <Dropdown.Toggle
                        variant="secondary"
                        className={`${styles.selectorInput} ${styles.premiumDropdownToggle} ${styles.premiumDropdownToggleStrong}`}
                      >
                        {renderPurchasePlanOptionLabel(
                          purchasePlanOptions.find((option) => option.value === purchasePlan) || purchasePlanOptions[0],
                        )}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className={styles.premiumDropdownMenu} align="start">
                        {purchasePlanOptions.map((option) => (
                          <Dropdown.Item
                            key={option.value}
                            className={`${styles.premiumDropdownItem} ${purchasePlan === option.value ? styles.premiumDropdownItemActive : ""}`}
                            onClick={() => setPurchasePlan(option.value)}
                            active={purchasePlan === option.value}
                          >
                            {renderPurchasePlanOptionLabel(option)}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <div className={styles.purchasePlanAmount}>
                      <div className={styles.priceRow}>
                        <span className={styles.currentPlanPrice}>{selectedPurchasePlanMeta.amount}</span>
                      </div>
                    </div>
                  </div>
                  {selectedPurchasePlanMeta.additionalSeat && (
                    <p className={styles.additionalSeatLine}>{selectedPurchasePlanMeta.additionalSeat}</p>
                  )}
                </Form.Group>

                {isSelectedPurchasePlanTeam && (
                  <>
                    <Form.Group className={styles.formGroupCompact}>
                      <div className={styles.teamPurchaseModeGroup} role="radiogroup" aria-label="Team purchase mode">
                        <Form.Check
                          id="team-purchase-mode-create"
                          type="radio"
                          className={`${styles.teamPurchaseModeOption} ${teamPurchaseMode === "create" ? styles.teamPurchaseModeOptionActive : ""}`}
                          name="team-purchase-mode"
                          label="Create a new team"
                          checked={teamPurchaseMode === "create"}
                          onChange={handleSelectCreateTeamMode}
                        />
                        <Form.Check
                          id="team-purchase-mode-existing"
                          type="radio"
                          className={`${styles.teamPurchaseModeOption} ${teamPurchaseMode === "existing" ? styles.teamPurchaseModeOptionActive : ""}`}
                          name="team-purchase-mode"
                          label="Select an existing team"
                          checked={teamPurchaseMode === "existing"}
                          onChange={handleSelectExistingTeamMode}
                          disabled={!hasExistingUnpaidTeams}
                        />
                      </div>
                    </Form.Group>

                    {teamPurchaseMode === "existing" && hasExistingUnpaidTeams ? (
                      <Form.Group className={styles.formGroupCompact}>
                        <Dropdown className={styles.premiumDropdown}>
                          <Dropdown.Toggle
                            variant="secondary"
                            className={`${styles.selectorInput} ${styles.premiumDropdownToggle} ${styles.teamExistingDropdownToggle}`}
                          >
                            {selectedExistingTeamOption ? (
                              <span className={styles.teamExistingSelectedText}>
                                {selectedExistingTeamOption.org_name || "Organization"}
                              </span>
                            ) : (
                              <span className={styles.teamSelectPlaceholder}>Select a team</span>
                            )}
                          </Dropdown.Toggle>
                          <Dropdown.Menu className={`${styles.premiumDropdownMenu} ${styles.teamExistingDropdownMenu}`} align="start">
                            {sortedExistingUnpaidTeamOptions.map((existingTeamOption) => (
                              <Dropdown.Item
                                key={existingTeamOption.key}
                                className={`${styles.premiumDropdownItem} ${styles.teamExistingDropdownItem} ${selectedExistingTeamKey === existingTeamOption.key ? styles.premiumDropdownItemActive : ""}`}
                                onClick={() => setSelectedExistingTeamKey(existingTeamOption.key)}
                                active={selectedExistingTeamKey === existingTeamOption.key}
                              >
                                {existingTeamOption.org_name || "Organization"}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      </Form.Group>
                    ) : teamPurchaseMode === "create" ? (
                      <Form.Group className={styles.formGroupCompact}>
                        <Form.Control
                          className={`${styles.textInput} ${styles.teamNameInput}`}
                          type="text"
                          value={teamOrgName}
                          onChange={(event) => setTeamOrgName(event.target.value)}
                          placeholder="Enter a team name"
                        />
                        {(hasInvalidCreateTeamName || hasDuplicateCreateTeamName) && (
                          <div className={styles.teamNameValidationText} role="alert" aria-live="polite">
                            {hasInvalidCreateTeamName ? (
                              <p className={styles.memberInputValidationText}>
                                {createTeamNameValidationMessage}
                              </p>
                            ) : matchedExistingTeamOption ? (
                              <button
                                type="button"
                                className={styles.teamNameSwitchBtn}
                                onClick={handleUseExistingMatchedTeam}
                              >
                                Team with this name already exists. Click here to select it.
                              </button>
                            ) : (
                              <p className={styles.memberInputValidationText}>
                                This team name is already in use for a team plan. Please choose another name.
                              </p>
                            )}
                          </div>
                        )}
                      </Form.Group>
                    ) : null}
                  </>
                )}

                <div className={`${styles.actionsRow} ${styles.purchaseActionsRow}`}>
                  <Button
                    as="a"
                    href="/pricing"
                    className={styles.secondaryActionBtn}
                  >
                    Learn about our pricing
                  </Button>

                  <Button
                    className={styles.primaryActionBtn}
                    onClick={() => void handlePurchase()}
                    disabled={busyAction !== null || overviewLoading || !canPurchaseSelectedTeamPlan}
                  >
                    {busyAction === "purchase" ? (
                      <>
                        <Spinner animation="border" size="sm" className={styles.buttonSpinner} />
                        Starting Checkout...
                      </>
                    ) : (
                      "Purchase"
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Your active subscriptions</div>
          {overviewLoading ? (
            <Placeholder animation="glow">
              <Placeholder className={styles.skeletonBar} xs={12} />
              <Placeholder className={styles.skeletonTitle} xs={4} />
              <Placeholder className={styles.skeletonBar} xs={8} />
              <Placeholder className={styles.skeletonBar} xs={6} />
            </Placeholder>
          ) : overviewError ? (
            <Alert variant="danger" className={styles.sectionAlert}>
              {overviewError}
              <div className={styles.actionsRow}>
                <Button className={styles.secondaryActionBtn} onClick={() => void loadOverview()}>
                  Retry
                </Button>
              </div>
            </Alert>
          ) : (
            <>
              <Dropdown className={styles.premiumDropdown}>
                <Dropdown.Toggle
                  variant="secondary"
                  className={`${styles.selectorInput} ${styles.premiumDropdownToggle}`}
                  disabled={options.length === 0}
                >
                  {selectedOption ? renderActiveSubscriptionOptionLabel(selectedOption) : "No subscriptions available"}
                </Dropdown.Toggle>
                <Dropdown.Menu className={styles.premiumDropdownMenu} align="start">
                  {options.map((option) => (
                    <Dropdown.Item
                      key={option.key}
                      className={`${styles.premiumDropdownItem} ${selectedOption?.key === option.key ? styles.premiumDropdownItemActive : ""}`}
                      onClick={() => setSelectedKey(option.key)}
                      active={selectedOption?.key === option.key}
                    >
                      {renderActiveSubscriptionOptionLabel(option)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <div className={styles.selectedDetailsSection}>
                {!selectedOption ? (
                  <p className={styles.emptyState}>No active subscriptions found.</p>
                ) : (
                  <>
                    <div className={styles.planHeaderRow}>
                      <div className={styles.planIdentityBadge}>
                        {selectedPlanIconKey && renderPlanIcon(selectedPlanIconKey, styles.planIcon)}
                        <span className={styles.planBadgeText}>{getPlanName(selectedOption.plan_code)}</span>
                      </div>
                      <div className={styles.planMetaStack}>
                        {shouldShowRenewal && (
                          <span className={styles.planRenewal}>Renews on {renewalDateLabel}</span>
                        )}
                        {selectedOption.scope === "business" && (
                          <span className={styles.planOrgInline}>Team Name: {selectedOption.org_name || "Organization"}</span>
                        )}
                      </div>
                    </div>

                    {selectedOption.scope === "business" && !selectedOption.is_owner_or_admin && selectedOption.managed_by_email && (
                      <div className={styles.metaGroup}>
                        <p className={styles.metaText}>Managed by: {selectedOption.managed_by_email}</p>
                      </div>
                    )}

                    {shouldShowFreeIndividualUpsellInfo && (
                      <Alert variant="info" className={`${styles.sectionAlert} ${styles.freePlanUpsellAlert}`}>
                        Your account doesn't have an active paid subscription. Purchase a plan to unlock premium benefits and keep your account active.
                      </Alert>
                    )}

                    {selectedOption.scope === "business" && selectedOption.is_owner_or_admin && (
                      <div className={styles.subSection}>
                        <div className={styles.subSectionTitle}>Manage Members</div>
                        {membersLoading ? (
                          <Placeholder animation="glow">
                            <Placeholder className={styles.skeletonBar} xs={12} />
                            <Placeholder className={styles.skeletonBar} xs={9} />
                          </Placeholder>
                        ) : membersError ? (
                          <Alert variant="danger" className={styles.sectionAlert}>
                            {membersError}
                            <div className={styles.actionsRow}>
                              <Button
                                className={styles.secondaryActionBtn}
                                onClick={() => void loadMembers(selectedOption.group_id)}
                              >
                                Retry
                              </Button>
                            </div>
                          </Alert>
                        ) : (
                          <>
                            <div className={styles.memberList}>
                              {activeMembers.map((member) => {
                                const roleDisplay = getMemberRoleDisplay(member.role);
                                return (
                                  <div key={member.user_id} className={styles.memberRow}>
                                    <span>{member.email}</span>
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip id={`member-role-tooltip-${member.user_id}`}>
                                          {roleDisplay.label}
                                        </Tooltip>
                                      }
                                    >
                                      <span
                                        className={`${styles.memberRoleIcon} ${roleDisplay.iconClassName}`}
                                        aria-label={roleDisplay.label}
                                      >
                                        {roleDisplay.icon}
                                      </span>
                                    </OverlayTrigger>
                                  </div>
                                );
                              })}
                            </div>
                            {activeMembers.length === 0 && (
                              <p className={styles.emptyState}>No active members found for this team yet.</p>
                            )}
                            {availableMemberSlots > 0 ? (
                              <>
                                <Form.Group className={styles.formGroupCompact}>
                                  <Form.Label className={styles.inputLabel}>
                                    Add up to {availableMemberSlots} more {availableMemberSlots === 1 ? "member" : "members"}
                                  </Form.Label>
                                  <div className={styles.memberInputList}>
                                    {memberInputs.slice(0, availableMemberSlots).map((memberInputRow, inputIndex) => (
                                      <div className={styles.memberInputRow} key={memberInputRow.id}>
                                        <Form.Control
                                          className={`${styles.textInput} ${styles.memberEmailInput}`}
                                          type="email"
                                          autoCapitalize="none"
                                          autoCorrect="off"
                                          spellCheck={false}
                                          name={`member-contact-${memberInputRow.id}`}
                                          autoComplete="email"
                                          value={memberInputRow.email}
                                          onChange={(event) => handleMemberInputChange(memberInputRow.id, event.target.value)}
                                          onBlur={() => handleMemberInputBlur(memberInputRow.id)}
                                          placeholder="member@example.com"
                                          isInvalid={(() => {
                                            if (!(memberValidationAttempted || memberInputRow.touched)) return false;
                                            const normalizedMemberEmail = normalizedMemberInputs[inputIndex];
                                            if (!normalizedMemberEmail) return false;
                                            if (!isValidEmailInput(memberInputRow.email)) return true;
                                            if (existingMemberEmailSet.has(normalizedMemberEmail)) return true;
                                            return (typedInputEmailCounts.get(normalizedMemberEmail) || 0) > 1;
                                          })()}
                                        />
                                        {memberInputs.length > 1 && (
                                          <OverlayTrigger
                                            placement="top"
                                            overlay={
                                              <Tooltip id={`remove-email-tooltip-${inputIndex}`}>
                                                Remove
                                              </Tooltip>
                                            }
                                          >
                                            <button
                                              type="button"
                                              className={styles.memberInputRemoveBtn}
                                              onClick={() => handleRemoveMemberInputRow(memberInputRow.id)}
                                              aria-label="Remove"
                                            >
                                              <RemoveIcon aria-hidden className={styles.memberInputRemoveIcon} />
                                            </button>
                                          </OverlayTrigger>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                  {memberInputValidationMessage && (
                                    <p className={styles.memberInputValidationText} role="alert" aria-live="polite">
                                      {memberInputValidationMessage}
                                    </p>
                                  )}
                                </Form.Group>
                                <div className={styles.memberActionsRow}>
                                  {memberInputs.length < availableMemberSlots && (
                                    <Button
                                      className={`${styles.secondaryActionBtn} ${styles.memberActionBtn}`}
                                      onClick={handleAddMemberInputRow}
                                      disabled={
                                        busyAction !== null
                                        || hasEmptyMemberEmailInput
                                        || hasInvalidMemberEmailInput
                                        || hasDuplicateMemberEmailInput
                                      }
                                    >
                                      Add Another Email
                                    </Button>
                                  )}
                                  <Button
                                    className={`${styles.secondaryActionBtn} ${styles.memberActionBtn}`}
                                    onClick={() => void handleAddMembers()}
                                    disabled={
                                      busyAction !== null
                                      || hasEmptyMemberEmailInput
                                      || hasInvalidMemberEmailInput
                                      || hasDuplicateMemberEmailInput
                                    }
                                  >
                                    Add Members
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <p className={styles.emptyState}>Team member limit reached. Remove a member to add someone new.</p>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {selectedHasPaidPlan && (
                      <div className={styles.subSection}>
                        <div className={styles.subSectionTitle}>Invoices</div>
                        {invoiceLoading ? (
                          <Placeholder animation="glow">
                            <Placeholder className={styles.skeletonBar} xs={12} />
                            <Placeholder className={styles.skeletonBar} xs={10} />
                            <Placeholder className={styles.skeletonBar} xs={8} />
                          </Placeholder>
                        ) : invoiceError ? (
                          <Alert variant="danger" className={styles.sectionAlert}>
                            {invoiceError}
                            <div className={styles.actionsRow}>
                              <Button
                                className={styles.secondaryActionBtn}
                                onClick={() => void loadInvoices(selectedOption.group_id)}
                              >
                                Retry
                              </Button>
                            </div>
                          </Alert>
                        ) : invoices.length === 0 ? (
                          <p className={styles.emptyState}>Invoices are not available yet for this plan.</p>
                        ) : (
                          <>
                            <div className={styles.invoiceList} aria-live="polite">
                              {visibleInvoices.map((invoice) => (
                                <div className={styles.invoiceItem} key={invoice.invoice_id}>
                                  <div className={styles.invoiceMetaRow}>
                                    <span className={styles.invoiceId}>#{invoice.invoice_id}</span>
                                    <span className={styles.invoiceStatus}>{invoice.status}</span>
                                  </div>
                                  <div className={styles.invoiceMetaRow}>
                                    <span>{formatDate(invoice.created_at)}</span>
                                    <span>{formatCurrency(invoice.amount_paid_cents, invoice.currency)}</span>
                                  </div>
                                  <div className={styles.invoiceActionsRow}>
                                    {invoice.hosted_invoice_url && (
                                      <Button
                                        className={`${styles.secondaryActionBtn} ${styles.invoiceActionBtn}`}
                                        size="sm"
                                        onClick={() =>
                                          window.open(
                                            invoice.hosted_invoice_url as string,
                                            "_blank",
                                            "noopener,noreferrer",
                                          )
                                        }
                                      >
                                        View Invoice
                                      </Button>
                                    )}
                                    {invoice.invoice_pdf_url && (
                                      <Button
                                        className={`${styles.secondaryActionBtn} ${styles.invoiceActionBtn}`}
                                        size="sm"
                                        onClick={() =>
                                          window.open(invoice.invoice_pdf_url as string, "_blank", "noopener,noreferrer")
                                        }
                                      >
                                        Download Invoice
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {hasOlderInvoices && (
                              <div className={styles.invoiceControlsRow}>
                                <button
                                  type="button"
                                  className={styles.invoiceToggleBtn}
                                  onClick={() => setShowOldInvoices((isExpanded) => !isExpanded)}
                                >
                                  {showOldInvoices
                                    ? "Hide past invoices"
                                    : `Show past invoice${oldInvoicesCount === 1 ? "" : "s"} (${oldInvoicesCount})`}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              </>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SubscriptionsBillingModal;
